import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Store, X } from 'lucide-react';
import SearchDropdown from './SearchDropdown';
import { getData } from '../utils/auth';
import AreaLineChart from './AreaLineChart';
import { getLocalCurrentDateTime, useDateHandler } from '../utils/dates';

const LocationComparer = ({ shops, handleClose, handleSearchSubmission }) => {
  const [filteredShops, setFilteredShops] = useState({});
  const { setUpDate, setUpAllDates, getDayCapacities } = useDateHandler();

  const handleSearchSubmit = async (id) => {
    if (!(id in filteredShops || Object.keys(filteredShops).length >= 5)) {
      let shopData = null;
      if (id.includes(':::')) {
        const info = id.split(' ::: ');
        shopData = await getData(`/locations/${info[0]}/shops/${info[1]}`);
      } else {
        shopData = await getData(`/locations/${id}`);
      }
      const currentDateTime = getLocalCurrentDateTime(shopData.latitude, shopData.longitude);
      const convertedCurrentDateTime = setUpDate(currentDateTime.dateTime);
      const convertedDateTimes = setUpAllDates(shopData.capacities);
      const currentCapacity = convertedDateTimes[convertedCurrentDateTime];
      const todayData = getDayCapacities(currentDateTime.day, convertedDateTimes);
      setFilteredShops({ ...filteredShops, [id]: { ...shopData, currentCapacity, todayData, currentDateTime } });
    }
  };

  const handleShopClick = (name, details) => {
    handleSearchSubmission(name, details);
  };

  const handleRemoveShop = (shopId) => {
    const updatedShops = { ...filteredShops };
    delete updatedShops[shopId];
    setFilteredShops(updatedShops);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay fullscreen"
      onClick={handleClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="location-modal fullscreen"
        onClick={(e) => e.stopPropagation()}
      >
        <AnimatePresence>
          <div className="modal-header fullscreen">
            <div className="header-left">
              <h1 className="location-title">Compare Locations</h1>
            </div>
            <div className="header-right">
              <button onClick={handleClose} className="icon-action-button close" title="Close">
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="shops-filters">
            <div className="compare-locations-search">
              {Object.keys(filteredShops).length >= 5 ? (
                <div className="max-shops-message">
                  <p>Maximum of 5 locations can be compared. Remove a location to add more.</p>
                </div>
              ) : (
                <SearchDropdown
                  options={shops}
                  onSelect={handleSearchSubmit}
                  selectedCategory={null}
                  onCategoryFilter={null}
                  onParkAndRideSelect={null}
                  onBicycleParkingSelect={null}
                  onMobilityParkingSelect={null}
                  onCarShareBayOperatorSelect={null}
                />
              )}
            </div>
          </div>
          {Object.keys(filteredShops).length > 0 ? (
            <div className="shops-table-container">
              <table className="shops-table">
                <thead>
                  <tr>
                    <th>Shop Name</th>
                    <th>Status</th>
                    <th>Crowd Levels</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(filteredShops).map((shopId) => {
                    const shop = filteredShops[shopId];
                    if (!shop) return null;
                    const currentCapacity = shop.currentCapacity;
                    const todayData = shop.todayData;
                    const isOpen = currentCapacity !== null && currentCapacity > 0;

                    return (
                      <tr
                        key={shopId}
                        className="shop-row clickable-row"
                        onClick={() => handleShopClick(shopId, filteredShops[shopId])}
                      >
                        <td>
                          <div className="shop-name-cell">
                            <strong>{shopId}</strong>
                            <span className="shop-click-hint">Click to view details</span>
                          </div>
                        </td>
                        <td>
                          <div className="shop-status-comparer">
                            {currentCapacity !== null ? (
                              <div className={`status-badge ${isOpen ? 'open' : 'closed'}`}>
                                {isOpen ? (
                                  <>
                                    <span className="status-dot"></span>
                                    Open • {currentCapacity}% busy
                                  </>
                                ) : (
                                  <>
                                    <span className="status-dot"></span>
                                    Closed
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className="status-unknown">Unknown</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="comparer-chart-cell">
                            <AreaLineChart
                              labels={todayData.map((item) => item[0].split(' ')[1].slice(0, -3))}
                              dataPoints={todayData.map((item) => Number(item[1]))}
                              showXScale={true}
                              showYScale={false}
                              currentTimeIndex={shop.currentDateTime.dateTime.split('T')[1].slice(0, -3)}
                            />
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering row click
                              handleRemoveShop(shopId);
                            }}
                            className="icon-action-button close"
                            title="Remove shop"
                          >
                            <X size={20} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-shops-found">
              <Store size={48} />
              <p>Add some locations to compare!</p>
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default LocationComparer;
